import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Share, Download, Check, Copy, Globe, Loader2, Code } from 'lucide-react';
import { toast } from 'sonner';
interface ShareBarProps {
  html: string;
  title: string;
  saving: boolean;
  savedSlug?: string | null;
  onSave: (status: 'draft' | 'archived') => void;
}
export function ShareBar({ html, title, saving, savedSlug, onSave }: ShareBarProps) {
  const [copied, setCopied] = useState(false);
  const [copiedHtml, setCopiedHtml] = useState(false);
  const downloadFile = () => {
    if (savedSlug) {
      window.open(`/api/doc/${savedSlug}/download`, '_blank');
      return;
    }
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'manuscript'}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded to local drive.');
  };
  const copyUrl = () => {
    const url = `${window.location.origin}/doc/${savedSlug}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Share link copied.');
    setTimeout(() => setCopied(false), 2200);
  };
  const copyRawHtml = () => {
    navigator.clipboard.writeText(html);
    setCopiedHtml(true);
    toast.success('Raw HTML code copied.');
    setTimeout(() => setCopiedHtml(false), 2200);
  };
  return (
    <div className="flex items-center gap-2">
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={copyRawHtml} 
        className="bg-white/10 text-white hover:bg-white/20 brand-monospace text-[9px] uppercase hidden md:flex"
      >
        {copiedHtml ? <Check className="w-3 h-3 mr-2" /> : <Code className="w-3 h-3 mr-2" />}
        {copiedHtml ? 'Copied HTML' : 'Copy HTML'}
      </Button>
      {!savedSlug ? (
        <>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={downloadFile} 
            className="bg-white/10 text-white hover:bg-white/20 brand-monospace text-[9px] uppercase"
          >
            <Download className="w-3 h-3 mr-2" /> Download
          </Button>
          <div className="flex bg-secondary rounded-lg shadow-md overflow-hidden">
            <Button 
              onClick={() => onSave('draft')} 
              disabled={saving} 
              className="bg-secondary text-white hover:bg-secondary/90 font-bold brand-monospace text-[9px] uppercase px-4 rounded-none border-r border-white/20"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share className="w-3 h-3 mr-2" />}
              Save Draft
            </Button>
            <Button 
              onClick={() => onSave('archived')} 
              disabled={saving} 
              title="Finalize and move to Archives"
              className="bg-secondary text-white hover:bg-secondary/90 font-bold brand-monospace text-[9px] uppercase px-3 rounded-none"
            >
              <Check className="w-3 h-3" />
            </Button>
          </div>
        </>
      ) : (
        <>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={downloadFile} 
            className="bg-white/10 text-white hover:bg-white/20 brand-monospace text-[9px] uppercase"
          >
            <Download className="w-3 h-3 mr-2" /> Export
          </Button>
          <Button 
            variant="ghost" 
            onClick={copyUrl} 
            className="text-secondary hover:text-secondary-fixed-dim font-bold brand-monospace text-[9px] uppercase px-3"
          >
            {copied ? <Check className="w-3 h-3 mr-2" /> : <Copy className="w-3 h-3 mr-2" />}
            {copied ? 'Copied' : 'Share Link'}
          </Button>
          <Button 
            variant="ghost" 
            asChild 
            className="text-tertiary hover:text-tertiary-fixed-dim font-bold brand-monospace text-[9px] uppercase px-3"
          >
            <a href={`/doc/${savedSlug}`} target="_blank" rel="noreferrer">
              <Globe className="w-3 h-3 mr-2" /> View Public
            </a>
          </Button>
        </>
      )}
    </div>
  );
}